// [S-316 / E-RBAC] Client-side capability resolution — the web twin of
// packages/core/src/auth/requireCapability.ts. Every UI gate authorizes on a
// CAPABILITY, never a role name, so a tenant persona (custom role) with, say,
// `template:author` is treated identically to a tenant_admin for that action.
//
// The effective set comes from the session's explicit `capabilities` (a custom
// role's grant, resolved per active tenant), falling back to the built-in
// role's default (ROLE_CAPABILITIES) when the session predates the claim. This
// mirrors effectiveCapabilities() server-side exactly, so the UI and the API
// agree on every decision.
import { ROLE_CAPABILITIES, type Capability } from "@csai/core/browser";
import type { WebSession } from "./types";

/** The capability set the session carries for its ACTIVE tenant. */
export function effectiveCapabilities(
  session: WebSession
): readonly Capability[] {
  return session.capabilities ?? ROLE_CAPABILITIES[session.role] ?? [];
}

/**
 * Does the session authorize `capability`? Mirrors core `hasCapability`:
 *   - the effective set must include the capability, AND
 *   - scope must reach the resource:
 *       platform_admin → any resource (platform/L1 included)
 *       tenant-scoped  → only within their own tenant; NEVER a platform/L1
 *                        resource (opts.layer === "L1").
 *
 * `opts.layer` defaults to "L2" (a tenant-scoped resource), which is what
 * almost every screen operates on. Pass "L1" for the platform template library
 * / other platform-owned resources — those stay platform_admin-only, matching
 * requireCapability's `scope.tenantId === null` branch.
 */
export function sessionCan(
  session: WebSession | null | undefined,
  capability: Capability,
  opts: { layer?: "L1" | "L2" } = {}
): boolean {
  if (session === null || session === undefined) return false;
  if (!effectiveCapabilities(session).includes(capability)) return false;
  if (session.role === "platform_admin") return true;
  // Tenant-scoped principal: a platform/L1 resource is out of reach.
  if (opts.layer === "L1") return false;
  return session.studioId !== null;
}

// Where a capability must be exercised — the UI twin of requireCapability's
// scope arg, used by route/section gates:
//   "any"      → the caller holds the cap with reach (platform_admin OR a tenant
//                member of the active tenant). The common case.
//   "tenant"   → a TENANT-owned resource that platform staff don't act on in the
//                UI. Requires the cap AND an own studio, so platform_admin (whose
//                studioId is null) is excluded — preserving routes that today use
//                an exact `roles={["tenant_admin"]}` allowlist.
//   "platform" → a platform/L1-owned resource. Only a principal with platform
//                reach passes (platform_admin), matching `scope.tenantId === null`.
export type CapabilityScope = "any" | "tenant" | "platform";

/** Scope-aware gate used by RequireCapability and section-level UI guards. */
export function sessionAuthorized(
  session: WebSession | null | undefined,
  capability: Capability,
  scope: CapabilityScope = "any"
): boolean {
  if (session === null || session === undefined) return false;
  switch (scope) {
    case "platform":
      return sessionCan(session, capability, { layer: "L1" });
    case "tenant":
      return (
        sessionCan(session, capability, { layer: "L2" }) &&
        session.studioId !== null
      );
    case "any":
    default:
      return sessionCan(session, capability, { layer: "L2" });
  }
}
